import { CommonModule } from '@angular/common';
import { Component, computed, inject, model, signal } from '@angular/core';
import {
  ButtonComponent,
  ButtonSize,
  ButtonType,
} from '@app/components/button';
import { DashedContentComponent } from '@app/components/dashed-content';
import { DialogComponent } from '@app/components/dialog';
import {
  InputDirection,
  InputSize,
  InputType,
  InputWidth,
} from '@app/components/input';
import {
  TextAlign,
  TextColor,
  TextComponent,
  TextHeight,
  TextSize,
  TextWeight,
} from '@app/components/text';
import { TextInputComponent } from '@app/components/text-input';
import {
  ViewAlign,
  ViewComponent,
  ViewDirection,
  ViewJustify,
} from '@app/components/view';
import { BatchResult } from '@app/services/admin-eligibility-file-import/admin-eligibility-file-import.model';
import {
  AdminEligibilityFileImportService,
  FileDetail,
  MAX_ALLOWED_SIZE,
} from '@app/services/admin-eligibility-file-import/admin-eligibility-file-import.service';
import { Spacing } from '@app/shared/types';
import { TranslatePipe } from '@ngx-translate/core';

type View = 'upload' | 'process' | 'invites';

enum InviteUserResultStatus {
  SUCCESS = 'success',
  FAIL = 'fail',
  PENDING = 'pending',
}
@Component({
  standalone: true,
  selector: 'app-eligibility-file',
  templateUrl: './eligibility-file.component.html',
  styleUrl: './eligibility-file.component.css',
  imports: [
    CommonModule,
    TranslatePipe,
    TextInputComponent,
    DialogComponent,
    TextComponent,
    ViewComponent,
    DashedContentComponent,
    ButtonComponent,
  ],
})
export class EligibilityFileComponent {
  eligibilityFilesImportService = inject(AdminEligibilityFileImportService);
  view = model<View>('upload');
  isOpen = model<boolean>(false);
  draggingNumOfFiles = signal(0);
  isRemovalPreviewMode = signal(false);

  isDraggingFilesOverDropZone = computed(() => !!this.draggingNumOfFiles());
  showDroppedFilesSection = computed(
    () => this.eligibilityFilesImportService.processedFiles().length > 0
  );
  totalFileSizeUploaded = signal(0);
  submitDisabled = computed(
    () => this.eligibilityFilesImportService.emailsToSubmit().size === 0
  );
  batches = computed(() => this.eligibilityFilesImportService.state().batches);
  isProcessing = computed(
    () => this.eligibilityFilesImportService.state().isProcessing
  );
  globalStatus = computed(
    () => this.eligibilityFilesImportService.state().globalStatus
  );
  allBatchesComplete = computed(() => {
    return (
      this.globalStatus() === 'completed' || this.globalStatus() === 'stopped'
    );
  });
  showResultsButton = computed(
    () => this.allBatchesComplete() && !this.isProcessing()
  );
  successfulEmails = computed(() =>
    this.eligibilityFilesImportService
      .state()
      .batches.flatMap((b) => b.emails.filter((e) => e.status === 'succeeded'))
  );
  failedEmails = computed(() =>
    this.eligibilityFilesImportService
      .state()
      .batches.flatMap((b) => b.emails.filter((e) => e.status === 'failed'))
  );
  unprocessedEmails = computed(() =>
    this.eligibilityFilesImportService.unprocessedEmailsList()
  );
  allInviteResults = computed(() => {
    const successful = this.successfulEmails().map((e) => ({
      email: e.email,
      status: InviteUserResultStatus.SUCCESS,
      message: e.message,
    }));
    const failed = this.failedEmails().map((e) => ({
      email: e.email,
      status: InviteUserResultStatus.FAIL,
      message: e.message,
    }));
    const unprocessed = this.unprocessedEmails().map((email) => ({
      email,
      status: InviteUserResultStatus.PENDING,
      message: 'Not processed',
    }));
    return [...successful, ...failed, ...unprocessed];
  });

  acceptedDomains: string;
  emailRegex: RegExp;
  isEmailRegexWildcard = false;
  ViewJustify = ViewJustify;
  ViewAlign = ViewAlign;
  ViewDirection = ViewDirection;
  ButtonType = ButtonType;
  ButtonSize = ButtonSize;
  InputType = InputType;
  InputSize = InputSize;
  InputDirection = InputDirection;
  InputWidth = InputWidth;
  Spacing = Spacing;
  TextSize = TextSize;
  TextHeight = TextHeight;
  TextWeight = TextWeight;
  TextColor = TextColor;
  TextAlign = TextAlign;
  InviteUserResultStatus = InviteUserResultStatus;

  isFileValid(file: FileDetail) {
    return file.errors?.length === 0;
  }

  getFileMessage(file) {
    if (!this.isFileValid(file)) {
      return file.errors[0].message;
    }

    return `${file.emailsParsed} records processed | ${file.valuesIgnored} values ignored`;
  }

  bytesToKilobytes(bytes: number) {
    return bytes / 1024;
  }

  bytesToMegabytes(bytes: number) {
    const BYTES_IN_MEGABYTE = 1048576;
    const megabytes = bytes / BYTES_IN_MEGABYTE;
    return Number.parseFloat(megabytes.toFixed(2));
  }

  getTotalUploadedMessage() {
    const uploadedBytes =
      this.eligibilityFilesImportService.totalFileSizeUploaded();
    const maxBytes = MAX_ALLOWED_SIZE;

    if (uploadedBytes < 1048576) {
      const uploadedKB = this.bytesToKilobytes(uploadedBytes);
      const maxMB = this.bytesToMegabytes(maxBytes);
      return `${uploadedKB.toFixed(2)}KB of ${maxMB}MB`;
    }

    const uploadedMB = this.bytesToMegabytes(uploadedBytes);
    const maxMB = this.bytesToMegabytes(maxBytes);
    return `${uploadedMB} of ${maxMB}MB`;
  }

  getTotalParsedMessage() {
    const totalEmailsToSubmit =
      this.eligibilityFilesImportService.emailsToSubmit().size;
    const totalDuplicateEmails =
      this.eligibilityFilesImportService.duplicateEmails().length;
    return `${totalEmailsToSubmit} records processed ${
      totalDuplicateEmails > 0
        ? `, ${totalDuplicateEmails} duplicates ignored`
        : ''
    }`;
  }

  preventDefault(event: Event) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragOver(e: DragEvent) {
    this.preventDefault(e);
  }

  onDragEnter(e: DragEvent) {
    this.preventDefault(e);
    const fileList = e.dataTransfer?.items;
    this.draggingNumOfFiles.set(fileList?.length ?? 0);
  }

  onDragLeave(e: DragEvent) {
    this.preventDefault(e);
    this.draggingNumOfFiles.set(0);
  }

  async onDrop(e: DragEvent) {
    this.preventDefault(e);
    this.draggingNumOfFiles.set(0);

    const fileList = e.dataTransfer?.files;
    try {
      this.eligibilityFilesImportService.handleCSVFilesImport(fileList);
    } catch (err) {
      console.error('Something went wrong when processing dropped files', err);
    }
  }

  async handlePrepareInvites() {
    this.isRemovalPreviewMode.set(false);
    this.eligibilityFilesImportService.operationMode.set('invite');
    this.eligibilityFilesImportService.prepareBatches();
    this.view.set('process');
    await this.eligibilityFilesImportService.runAllBatches();
  }

  handleViewResults() {
    this.view.set('invites');
  }

  getBatchStatusIcon(batchStatus: string) {
    switch (batchStatus) {
      case 'succeeded':
        return 'success';
      case 'failed':
        return 'error';
      case 'partial':
        return 'warning';
      case 'running':
      case 'retrying':
        return 'pending';
      default:
        return 'pending';
    }
  }

  getBatchStatusText(batch: BatchResult) {
    const emailCount = batch.emails.length;
    const succeededCount = batch.emails.filter(
      (e: any) => e.status === 'succeeded'
    ).length;
    const failedCount = batch.emails.filter(
      (e: any) => e.status === 'failed'
    ).length;

    if (batch.status === 'succeeded') {
      return `${succeededCount} succeeded`;
    } else if (batch.status === 'failed') {
      return `${failedCount} failed`;
    } else if (batch.status === 'partial') {
      return `${succeededCount} succeeded, ${failedCount} failed`;
    } else if (batch.status === 'running') {
      return 'Processing...';
    } else if (batch.status === 'retrying') {
      return 'Retrying...';
    }
    return `${emailCount} pending`;
  }

  handleDone(): void {
    this.eligibilityFilesImportService.clearProcessedFiles();
    this.isRemovalPreviewMode.set(false);
    this.isOpen.set(false);
  }

  handleDownloadFailedEmails(): void {
    this.eligibilityFilesImportService.downloadFailedEmailsCSV();
  }

  handleDeleteUsers(): void {
    this.isRemovalPreviewMode.set(true);
    this.eligibilityFilesImportService.operationMode.set('remove');
    this.eligibilityFilesImportService.prepareBatches();
    this.view.set('process');
  }

  async handleContinueWithRemoval(): Promise<void> {
    this.isRemovalPreviewMode.set(false);
    await this.eligibilityFilesImportService.runAllBatches();
  }

  async handleSelectFile(event: Event) {
    this.preventDefault(event);
    const fileInput = event.target as HTMLInputElement;
    const fileList = fileInput.files;
    try {
      this.eligibilityFilesImportService.handleCSVFilesImport(fileList);
    } catch (err) {
      console.error('Something went wrong when processing dropped files', err);
    }

    this.draggingNumOfFiles.set(0);
  }
}
